import React, { useState } from 'react';
import { useCountdown } from '../hooks/useCountdown';
import { X, Mail, Smartphone, Command, ArrowRight, Sparkles, Calendar, ChevronRight, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useLanguage } from '../i18nContext';
import { login, loginWithCode, loginWithPhone, sendVerificationCode, sendSmsCode } from '../services/auth';

interface LoginModalProps {
  onClose: () => void;
  onLogin: () => void;
  onSwitchToSignUp?: (prefill?: { method: 'email' | 'phone'; contact: string; code: string }) => void;
  onForgotPassword?: () => void;
  onNavigate?: (page: string) => void;
}

type LoginMethod = 'password' | 'emailCode' | 'phoneCode';

export default function LoginModal({ onClose, onLogin, onSwitchToSignUp, onForgotPassword, onNavigate }: LoginModalProps) {
  const [method, setMethod] = useState<LoginMethod>('password');
  const { t, language } = useLanguage();

  // 表单状态
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // 加载状态
  const [loading, setLoading] = useState(false);
  const [sendingEmailCode, setSendingEmailCode] = useState(false);
  const [sendingPhoneCode, setSendingPhoneCode] = useState(false);

  // 倒计时
  const [emailCountdown, startEmailCountdown] = useCountdown(60);
  const [phoneCountdown, startPhoneCountdown] = useCountdown(60);

  // 错误信息
  const [error, setError] = useState('');

  // 发送邮箱验证码
  const handleSendEmailCode = async () => {
    if (!email) {
      setError(language === 'zh' ? '请输入邮箱' : 'Please enter email');
      return;
    }
    setSendingEmailCode(true);
    setError('');
    try {
      await sendVerificationCode(email);
      startEmailCountdown();
    } catch (e: any) {
      setError(e.message || (language === 'zh' ? '发送失败' : 'Failed to send'));
    } finally {
      setSendingEmailCode(false);
    }
  };

  // 发送手机验证码
  const handleSendPhoneCode = async () => {
    if (!phone) {
      setError(language === 'zh' ? '请输入手机号' : 'Please enter phone number');
      return;
    }
    setSendingPhoneCode(true);
    setError('');
    try {
      await sendSmsCode(phone, 'login');
      startPhoneCountdown();
    } catch (e: any) {
      setError(e.message || (language === 'zh' ? '发送失败' : 'Failed to send'));
    } finally {
      setSendingPhoneCode(false);
    }
  };

  // 处理登录
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (method === 'password') {
        if (!account || !password) {
          setError(language === 'zh' ? '请填写完整信息' : 'Please fill in all fields');
          setLoading(false);
          return;
        }
        await login({ account, password });
      } else if (method === 'emailCode') {
        if (!email || !emailCode) {
          setError(language === 'zh' ? '请填写完整信息' : 'Please fill in all fields');
          setLoading(false);
          return;
        }
        await loginWithCode({ email, verificationCode: emailCode });
      } else if (method === 'phoneCode') {
        if (!phone || !phoneCode) {
          setError(language === 'zh' ? '请填写完整信息' : 'Please fill in all fields');
          setLoading(false);
          return;
        }
        await loginWithPhone({ phone, code: phoneCode });
      }
      onLogin();
    } catch (e: any) {
      const msg = e.message || '';
      if (msg.includes('USER_NOT_FOUND')) {
        if (method === 'emailCode') {
          onSwitchToSignUp?.({ method: 'email', contact: email, code: emailCode });
        } else if (method === 'phoneCode') {
          onSwitchToSignUp?.({ method: 'phone', contact: phone, code: phoneCode });
        } else {
          setError(language === 'zh' ? '用户不存在，请先注册' : 'User not found, please sign up');
        }
      } else {
        setError(msg || (language === 'zh' ? '登录失败' : 'Login failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  // 切换登录方式时清空错误
  const handleMethodChange = (newMethod: LoginMethod) => {
    setMethod(newMethod);
    setError('');
  };

  const methodLabels = {
    password: language === 'zh' ? '密码登录' : 'Password',
    emailCode: language === 'zh' ? '邮箱验证码' : 'Email Code',
    phoneCode: language === 'zh' ? '手机验证码' : 'Phone Code',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 isolate">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-brand-900/10 backdrop-blur-md transform-gpu transition-opacity duration-300 pointer-events-auto"
        style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
        onClick={onClose}
      ></div>

      <div className="relative w-full max-w-5xl bg-white rounded-modal shadow-modal overflow-hidden flex flex-col md:flex-row h-auto max-h-[90vh] animate-in fade-in zoom-in-95 duration-500 border border-white z-10">

        {/* Left Side: Brand Panel */}
        <div className="w-full md:w-[45%] bg-surface-light p-10 md:p-14 flex flex-col relative overflow-hidden shrink-0 border-r border-surface-border">
          <div className="absolute top-[-50px] left-[-50px] w-80 h-80 bg-brand-200/30 rounded-full blur-[80px]"></div>
          <div className="absolute bottom-[-50px] right-[-50px] w-80 h-80 bg-prestige-300/20 rounded-full blur-[80px]"></div>
          <div className="absolute inset-0 bg-noise opacity-[0.03] pointer-events-none"></div>

          <div className="relative z-10 mb-12">
            <div className="w-12 h-12 bg-brand-900 rounded-xl flex items-center justify-center text-white shadow-lg mb-6">
              <Command size={24} strokeWidth={2.5} />
            </div>
            <h1 className="text-4xl font-serif font-bold text-brand-900 mb-4 tracking-tight leading-tight">
              {t.auth.welcomeBack}
            </h1>
            <p className="text-ink-secondary text-lg font-medium opacity-80 max-w-[240px]">
              {t.auth.insightsWaiting}
            </p>
          </div>

          {/* What's New Section */}
          <div className="relative z-10 mt-auto bg-white/60 backdrop-blur-sm border border-surface-border rounded-3xl p-6 shadow-sm group/update hover:shadow-md transition-all">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-brand-900 rounded-lg flex items-center justify-center text-white shadow-sm">
                  <Sparkles size={16} fill="currentColor" />
                </div>
                <span className="text-[10px] font-bold text-brand-900 uppercase tracking-widest">{t.auth.whatsNew}</span>
              </div>
              <div className="flex items-center gap-1.5 text-[10px] font-bold text-ink-muted uppercase tracking-widest">
                <Calendar size={12} />
                {t.auth.march2025}
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-lg font-serif font-bold text-brand-900">{t.auth.consultingEngineTitle}</h4>
              <p className="text-xs text-ink-secondary leading-relaxed mb-4 font-medium opacity-90">
                {t.auth.consultingEngineDesc}
              </p>

              <button onClick={() => { onClose(); onNavigate?.('changelog'); }} className="flex items-center gap-1 text-[10px] font-bold text-brand-700 hover:text-brand-900 transition-colors uppercase tracking-widest">
                {t.auth.releaseNotes}
                <ChevronRight size={10} className="group-hover/update:translate-x-0.5 transition-transform" />
              </button>
            </div>
          </div>

          <div className="relative z-10 mt-8 flex items-center gap-2 text-[10px] font-bold text-ink-muted uppercase tracking-widest pl-1">
            <div className="w-1 h-1 rounded-full bg-brand-500"></div>
            {t.auth.systemsOperational}
          </div>
        </div>

        {/* Right Side: Login Form */}
        <div className="flex-1 p-10 md:p-14 bg-white flex flex-col justify-center relative overflow-y-auto">
          <button
            onClick={onClose}
            className="absolute top-8 right-8 p-2 text-gray-300 hover:text-brand-900 rounded-full hover:bg-gray-50 transition-all z-20"
          >
            <X size={20} />
          </button>

          <div className="mb-8 text-center md:text-left">
            <h2 className="text-3xl font-serif font-bold text-brand-900 mb-2">{t.auth.login}</h2>
            <p className="text-gray-400 text-sm font-medium">{t.auth.welcomeSub}</p>
          </div>

          {/* 三种登录方式切换 */}
          <div className="flex p-1 bg-surface rounded-2xl mb-6 border border-surface-border">
            <button
              onClick={() => handleMethodChange('password')}
              className={`flex-1 py-2.5 text-[11px] font-bold rounded-xl transition-all flex items-center justify-center gap-1.5
              ${method === 'password'
                  ? 'bg-white text-brand-900 shadow-sm ring-1 ring-surface-border'
                  : 'text-gray-400 hover:text-gray-600'}`}
            >
              <Lock size={12} /> {methodLabels.password}
            </button>
            <button
              onClick={() => handleMethodChange('emailCode')}
              className={`flex-1 py-2.5 text-[11px] font-bold rounded-xl transition-all flex items-center justify-center gap-1.5
              ${method === 'emailCode'
                  ? 'bg-white text-brand-900 shadow-sm ring-1 ring-surface-border'
                  : 'text-gray-400 hover:text-gray-600'}`}
            >
              <Mail size={12} /> {methodLabels.emailCode}
            </button>
            <button
              onClick={() => handleMethodChange('phoneCode')}
              className={`flex-1 py-2.5 text-[11px] font-bold rounded-xl transition-all flex items-center justify-center gap-1.5
              ${method === 'phoneCode'
                  ? 'bg-white text-brand-900 shadow-sm ring-1 ring-surface-border'
                  : 'text-gray-400 hover:text-gray-600'}`}
            >
              <Smartphone size={12} /> {methodLabels.phoneCode}
            </button>
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">
              {error}
            </div>
          )}

          <form className="space-y-5" onSubmit={handleSubmit}>
            {/* 密码登录表单 */}
            {method === 'password' && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                    {language === 'zh' ? '手机号/邮箱/用户名' : 'Phone/Email/Username'}
                  </label>
                  <input
                    type="text"
                    value={account}
                    onChange={(e) => setAccount(e.target.value)}
                    placeholder=""
                    className="w-full h-12 px-4 rounded-xl border border-surface-border bg-surface text-sm focus:bg-white focus:border-brand-900 focus:ring-4 focus:ring-brand-900/5 transition-all outline-none"
                  />
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between items-center px-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t.auth.password}</label>
                    <button
                      type="button"
                      onClick={onForgotPassword}
                      disabled={!account.trim()}
                      className={`text-[10px] font-bold uppercase tracking-widest ${account.trim() ? 'text-gray-400 hover:text-brand-900' : 'text-gray-300 cursor-not-allowed'}`}
                    >
                      {language === 'zh' ? '忘记密码?' : 'Forgot?'}
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder=""
                      className="w-full h-12 px-4 pr-12 rounded-xl border border-surface-border bg-surface text-sm focus:bg-white focus:border-brand-900 focus:ring-4 focus:ring-brand-900/5 transition-all outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* 邮箱验证码登录表单 */}
            {method === 'emailCode' && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                    {language === 'zh' ? '邮箱地址' : 'Email Address'}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder=""
                    className="w-full h-12 px-4 rounded-xl border border-surface-border bg-surface text-sm focus:bg-white focus:border-brand-900 focus:ring-4 focus:ring-brand-900/5 transition-all outline-none"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                    {language === 'zh' ? '验证码' : 'Verification Code'}
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={emailCode}
                      onChange={(e) => setEmailCode(e.target.value)}
                      placeholder=""
                      className="flex-1 h-12 px-4 rounded-xl border border-surface-border bg-surface text-sm focus:bg-white focus:border-brand-900 focus:ring-4 focus:ring-brand-900/5 transition-all outline-none"
                    />
                    <button
                      type="button"
                      onClick={handleSendEmailCode}
                      disabled={sendingEmailCode || emailCountdown > 0}
                      className="px-4 h-12 bg-surface border border-surface-border rounded-xl text-sm font-bold text-brand-900 hover:bg-surface-border disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap"
                    >
                      {sendingEmailCode ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : emailCountdown > 0 ? (
                        `${emailCountdown}s`
                      ) : (
                        language === 'zh' ? '获取验证码' : 'Get Code'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* 手机验证码登录表单 */}
            {method === 'phoneCode' && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                    {language === 'zh' ? '手机号' : 'Phone Number'}
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder=""
                    className="w-full h-12 px-4 rounded-xl border border-surface-border bg-surface text-sm focus:bg-white focus:border-brand-900 focus:ring-4 focus:ring-brand-900/5 transition-all outline-none"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                    {language === 'zh' ? '验证码' : 'Verification Code'}
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={phoneCode}
                      onChange={(e) => setPhoneCode(e.target.value)}
                      placeholder=""
                      className="flex-1 h-12 px-4 rounded-xl border border-surface-border bg-surface text-sm focus:bg-white focus:border-brand-900 focus:ring-4 focus:ring-brand-900/5 transition-all outline-none"
                    />
                    <button
                      type="button"
                      onClick={handleSendPhoneCode}
                      disabled={sendingPhoneCode || phoneCountdown > 0}
                      className="px-4 h-12 bg-surface border border-surface-border rounded-xl text-sm font-bold text-brand-900 hover:bg-surface-border disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap"
                    >
                      {sendingPhoneCode ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : phoneCountdown > 0 ? (
                        `${phoneCountdown}s`
                      ) : (
                        language === 'zh' ? '获取验证码' : 'Get Code'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full relative h-14 bg-brand-900 hover:bg-brand-800 text-white rounded-2xl text-base font-bold shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 group flex items-center justify-center gap-3 overflow-hidden disabled:opacity-70 disabled:cursor-not-allowed"
              >
                <div className="absolute inset-0 bg-noise opacity-10 mix-blend-overlay"></div>
                {loading ? (
                  <Loader2 size={20} className="animate-spin relative z-10" />
                ) : (
                  <>
                    <span className="relative z-10">{t.auth.login}</span>
                    <ArrowRight size={18} className="relative z-10 group-hover:translate-x-1 transition-transform stroke-[2.5]" />
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm font-medium">
              {t.auth.noAccount}{' '}
              <button onClick={() => onSwitchToSignUp?.()} className="text-brand-900 font-bold hover:underline ml-1">
                {t.auth.signup}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
